import { DesignerToolbarSettings } from '@shesha-io/reactjs';
import { nanoid } from 'nanoid';
import { ITEM_POSITIONS, MAP_STYLES } from './utils';

export const settingsForm = new DesignerToolbarSettings()
  .addTextField({
    id: nanoid(),
    propertyName: 'Name',
    parentId: 'root',
    label: 'Name',
    validate: { required: true },
  })
  .addTextField({
    id: nanoid(),
    propertyName: 'accessToken',
    parentId: 'root',
    label: 'Mapbox Access Token',
    validate: { required: true },
  })
  .addNumberField({
    id: nanoid(),
    propertyName: 'zoomLevel',
    parentId: 'root',
    label: 'Zoom Level',
    defaultValue: 15,
    validate: { required: true },
  })
  .addDropdown({
    id: nanoid(),
    propertyName: 'mapStyle',
    parentId: 'root',
    label: 'Map Style',
    validate: { required: false },
    dataSourceType: 'values',
    values: MAP_STYLES.map((style) => ({ id: nanoid(), value: style.name, label: style.label })),
    defaultValue: 'mapbox://styles/mapbox/streets-v12',
  })
  .addCheckbox({
    id: nanoid(),
    propertyName: 'enableSearch',
    parentId: 'root',
    label: 'Enable Search',
    validate: { required: false },
  })
  .addDropdown({
    id: nanoid(),
    propertyName: 'searchbarPosition',
    parentId: 'root',
    label: 'Search Bar Position',
    validate: { required: false },
    hidden: {
      _code: 'return getSettingValue(data?.enableSearch) !== true',
      _mode: 'code',
      _value: false,
    },
    dataSourceType: 'values',
    values: ITEM_POSITIONS.map((position) => ({ id: nanoid(), value: position.name, label: position.label })),
    defaultValue: 'top-left',
  })
  .addCheckbox({
    id: nanoid(),
    propertyName: 'enableCustomPanel',
    parentId: 'root',
    label: 'Enable Components Panel',
    validate: { required: false },
  })
  .addDropdown({
    id: nanoid(),
    propertyName: 'panelPosition',
    parentId: 'root',
    label: 'Panel Position',
    validate: { required: false },
    hidden: {
      _code: 'return getSettingValue(data?.enableCustomPanel) !== true',
      _mode: 'code',
      _value: false,
    },
    dataSourceType: 'values',
    values: ITEM_POSITIONS.map((position) => ({ id: nanoid(), value: position.name, label: position.label })),
    defaultValue: 'top-left',
  })
  .addSectionSeparator({
    id: nanoid(),
    propertyName: 'separator1',
    parentId: 'root',
    label: 'Map Event Handlers',
  })
  .addCodeEditor({
    id: nanoid(),
    propertyName: 'onGetBoundaries',
    parentId: 'root',
    label: 'Get Boundary data',
    description: 'Returns the boundaries as an array of features',
    language: 'javascript',
    wrapInTemplate: true,
    resultTypeExpression: async ({ metadataBuilder }) => {
      return metadataBuilder.array(
        'features',
        async (b) =>
          b
            .object('Feature', '')
            .addString('id', 'unique identifier of the feature')
            .addString('type', 'type of feature')
            .addObject('geometry', 'geometry', async (ob) =>
              ob
                .addString('type', 'type of geometry')
                .addArray('coordinates', 'Coordinates')
                .addNumber('coordinate', 'Coordinate')
                .build(),
            )
            .build(),
        'Boundaries to show on map',
      );
    },
    templateSettings: {
      useAsyncDeclaration: true,
      functionName: 'getBoundariesAsync',
    },
  })
  .addCodeEditor({
    id: nanoid(),
    propertyName: 'onGetCenter',
    parentId: 'root',
    label: 'Get Map Center',
    description: 'Returns the center of the map as a Promise of [Longitude, Latitude]',
    language: 'javascript',
    wrapInTemplate: true,
    resultTypeExpression: async ({ metadataBuilder }) => {
      return metadataBuilder.array('LngLatLike', async (b) => b.number(), 'Longitude and Latitude definition');
    },
    templateSettings: {
      useAsyncDeclaration: true,
      functionName: 'getMapCenterAsync',
    },
  })
  .addCodeEditor({
    id: nanoid(),
    propertyName: 'onSave',
    parentId: 'root',
    label: 'On Save',
    description: 'Function to call when the user clicks the Save button',
    language: 'javascript',
    availableConstantsExpression: async ({ metadataBuilder }) => {
      return metadataBuilder
        .object('Constants', 'Constants')
        .addAllStandard()
        .addArray('polygons', 'The boundaries drawn on the map')
        .build();
    },
    wrapInTemplate: true,
    templateSettings: {
      useAsyncDeclaration: true,
      functionName: 'onSaveAsync',
    },
  })
  .addSectionSeparator({
    id: nanoid(),
    propertyName: 'separator2',
    parentId: 'root',
    label: 'Style',
  })
  .addColorPicker({
    id: nanoid(),
    propertyName: 'boundaryColour',
    parentId: 'root',
    description: 'Colour of the drawn boundaries',
    label: 'Boundary Colour',
    validate: { required: false },
    allowClear: true,
    defaultValue: '#d5e0e2',
    disabledAlpha: true,
  })
  .addCodeEditor({
    id: nanoid(),
    propertyName: 'panelStyle',
    parentId: 'root',
    label: 'Panel Style',
    mode: 'dialog',
    description: 'Function to return the style of the panel',
    hidden: {
      _code: 'return getSettingValue(data?.enableCustomPanel) !== true',
      _mode: 'code',
      _value: false,
    },
  })
  .toJson();
